/** Layout du plateau + mapping clic→cellule. Repris du prototype (index.html:325-334, 2026-2041). */
import type { PuzzleBase } from '@ordre-cache/shared';

/** Sous-ensemble structurel du puzzle Vitrail lu par le hit-test (marges d'indices). */
interface VitrailLike {
  rowR: number[][];
  colR: number[][];
}

/** Côté du plateau en pixels CSS, d'après la zone disponible (index.html:325-328). */
export function computeBoardSize(area: HTMLElement): number {
  const rect = area.getBoundingClientRect();
  return Math.max(160, Math.floor(Math.min(rect.width, rect.height) - 6));
}

/** Configure le canvas au bon devicePixelRatio (plafonné à 2, docs/03 §7) et renvoie le ctx transformé. */
export function setupCanvas(canvas: HTMLCanvasElement, size: number): CanvasRenderingContext2D {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.style.width = size + 'px';
  canvas.style.height = size + 'px';
  canvas.width = Math.round(size * dpr);
  canvas.height = Math.round(size * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d indisponible');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

/**
 * Cellule (r, c) touchée pour un clic à (x, y) relatifs au canvas, plateau de côté `size`.
 * Reproduit la géométrie spéciale de Vitrail (marges d'indices) et Rotor (bande de vignette).
 * Fonction PURE → testable sans DOM. Renvoie null hors grille.
 */
export function hitCell(
  p: PuzzleBase,
  x: number,
  y: number,
  size: number,
): { r: number; c: number } | null {
  const N = p.N;
  let cs = size / N;
  let ox = 0;
  let oy = 0;
  if (p.g === 'vitrail') {
    const v = p as PuzzleBase & VitrailLike;
    const mR = Math.max(...v.rowR.map((a) => a.length));
    const mC = Math.max(...v.colR.map((a) => a.length));
    const padL = Math.max(1.6, mR * 0.62);
    const padT = Math.max(1.6, mC * 0.62);
    cs = Math.min(size / (N + padL), size / (N + padT));
    ox = padL * cs;
    oy = padT * cs;
  } else if (p.g === 'rotor') {
    const band = Math.max(50, size * 0.24);
    const gs = size - band;
    cs = gs / N;
    ox = (size - gs) / 2;
    oy = band;
  }
  const c = Math.floor((x - ox) / cs);
  const r = Math.floor((y - oy) / cs);
  if (r < 0 || c < 0 || r >= N || c >= N) return null;
  return { r, c };
}
